import { useState } from 'react';
import { Music, Plus, X, Check, Trash2, DollarSign, Calendar, Clock, Upload } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper to format date for datetime-local input
const formatDateForInput = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toISOString().slice(0, 16);
};

// Helper to check if a track is scheduled (future release)
const isScheduledRelease = (releaseDate) => {
  if (!releaseDate) return false;
  return new Date(releaseDate) > new Date();
};

// File upload helper
const uploadFile = async (file, category) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API_URL}/upload?category=${category}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data.url;
};

export default function AdminTracks({ tracks, albums, onRefresh }) {
  const [editingTrack, setEditingTrack] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showRoyaltyDialog, setShowRoyaltyDialog] = useState(false);
  const [selectedTrackForRoyalty, setSelectedTrackForRoyalty] = useState(null);
  const [royaltyAmount, setRoyaltyAmount] = useState('');
  const [uploading, setUploading] = useState({ audio: false, cover: false });
  const [newTrack, setNewTrack] = useState({
    title: '', album: '', album_id: '', duration: 180, audio_url: '', cover_url: '', price: 1.99, token_price: 5.00, release_date: ''
  });

  // Handle audio file upload
  const handleAudioUpload = async (e, isEditing = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('audio/')) {
      toast.error('Please select an audio file');
      return;
    }
    
    setUploading(prev => ({ ...prev, audio: true }));
    try {
      const url = await uploadFile(file, 'audio');
      if (isEditing) {
        setEditingTrack(prev => ({ ...prev, audio_url: url }));
      } else {
        setNewTrack(prev => ({ ...prev, audio_url: url }));
      }
      toast.success('Audio uploaded!');
    } catch (error) {
      toast.error('Failed to upload audio');
    } finally {
      setUploading(prev => ({ ...prev, audio: false }));
    }
  };

  // Handle cover image upload
  const handleCoverUpload = async (e, isEditing = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setUploading(prev => ({ ...prev, cover: true }));
    try {
      const url = await uploadFile(file, 'image');
      if (isEditing) {
        setEditingTrack(prev => ({ ...prev, cover_url: url }));
      } else {
        setNewTrack(prev => ({ ...prev, cover_url: url }));
      }
      toast.success('Cover uploaded!');
    } catch (error) {
      toast.error('Failed to upload cover');
    } finally {
      setUploading(prev => ({ ...prev, cover: false }));
    }
  };

  const createTrack = async () => {
    if (!newTrack.title) {
      toast.error('Title is required');
      return;
    }
    try {
      const trackData = { ...newTrack };
      // Convert local datetime to ISO string if release date is set
      if (trackData.release_date) {
        trackData.release_date = new Date(trackData.release_date).toISOString();
      } else {
        delete trackData.release_date;
      }
      await axios.post(`${API_URL}/admin/tracks`, trackData);
      toast.success('Track created!');
      setShowCreate(false);
      setNewTrack({ title: '', album: '', album_id: '', duration: 180, audio_url: '', cover_url: '', price: 1.99, token_price: 5.00, release_date: '' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create track');
    }
  };

  const saveTrack = async (track) => {
    try {
      await axios.put(`${API_URL}/admin/tracks/${track.id}`, {
        title: track.title,
        album: track.album,
        album_id: track.album_id,
        duration: track.duration,
        price: track.price,
        token_price: track.token_price,
        audio_url: track.audio_url,
        cover_url: track.cover_url,
        release_date: track.release_date || null
      });
      toast.success('Track updated');
      setEditingTrack(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update track');
    }
  };

  const deleteTrack = async (id) => {
    if (!window.confirm('Delete this track? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/admin/tracks/${id}`);
      toast.success('Track deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete track');
    }
  };

  const distributeRoyalties = async () => {
    if (!selectedTrackForRoyalty || !royaltyAmount) return;
    try {
      const today = new Date();
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      await axios.post(`${API_URL}/admin/royalties/distribute?track_id=${selectedTrackForRoyalty.id}&amount=${royaltyAmount}&period_start=${lastMonth.toISOString()}&period_end=${today.toISOString()}`);
      toast.success('Royalties distributed! Token holders notified.');
      setShowRoyaltyDialog(false);
      setRoyaltyAmount('');
      setSelectedTrackForRoyalty(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to distribute royalties');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Manage Tracks</h2>
        <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-track-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Track
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="glass-card p-6 mb-4">
          <h3 className="text-lg font-semibold mb-4">Create New Track</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input value={newTrack.title} onChange={(e) => setNewTrack({...newTrack, title: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Album</Label>
              <select value={newTrack.album_id} onChange={(e) => {
                const album = albums.find(a => a.id === e.target.value);
                setNewTrack({...newTrack, album_id: e.target.value, album: album?.title || ''});
              }} className="input-dark mt-1 w-full rounded-md" data-testid="track-album-select">
                <option value="">🎵 No Album (release as single)</option>
                {albums.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
              </select>
            </div>
            <div>
              <Label>Duration (seconds)</Label>
              <Input type="number" value={newTrack.duration} onChange={(e) => setNewTrack({...newTrack, duration: parseInt(e.target.value)})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Price ($)</Label>
              <Input type="number" step="0.01" value={newTrack.price} onChange={(e) => setNewTrack({...newTrack, price: parseFloat(e.target.value)})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Audio File</Label>
              <div className="flex gap-2 mt-1">
                <Input 
                  value={newTrack.audio_url} 
                  onChange={(e) => setNewTrack({...newTrack, audio_url: e.target.value})} 
                  className="input-dark flex-1" 
                  placeholder="Audio URL or upload file"
                />
                <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                  <Upload className="w-4 h-4" />
                  {uploading.audio ? 'Uploading...' : 'Upload'}
                  <input type="file" className="hidden" accept="audio/*" onChange={(e) => handleAudioUpload(e, false)} disabled={uploading.audio} />
                </label>
              </div>
              {newTrack.audio_url && <p className="text-xs text-green-500 mt-1">✓ Audio file set</p>}
            </div>
            <div>
              <Label>Cover Image</Label>
              <div className="flex gap-2 mt-1">
                <Input 
                  value={newTrack.cover_url} 
                  onChange={(e) => setNewTrack({...newTrack, cover_url: e.target.value})} 
                  className="input-dark flex-1" 
                  placeholder="Cover URL or upload image"
                />
                <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                  <Upload className="w-4 h-4" />
                  {uploading.cover ? 'Uploading...' : 'Upload'}
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleCoverUpload(e, false)} disabled={uploading.cover} />
                </label>
              </div>
              {newTrack.cover_url && (
                <div className="mt-2">
                  <img src={newTrack.cover_url} alt="Cover preview" className="w-16 h-16 rounded object-cover" />
                </div>
              )}
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Release Date (Optional)
              </Label>
              <Input 
                type="datetime-local" 
                value={newTrack.release_date} 
                onChange={(e) => setNewTrack({...newTrack, release_date: e.target.value})} 
                className="input-dark mt-1" 
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty for immediate release</p>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button onClick={createTrack} className="btn-primary"><Plus className="w-4 h-4 mr-2" /> Create</button>
          </div>
        </div>
      )}

      {/* Track List */}
      {tracks.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Music className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Tracks Yet</h3>
          <p className="text-gray-400 mb-4">Add your first track to get started</p>
        </div>
      ) : (
        tracks.map(track => (
          <div key={track.id} className="glass-card p-4" data-testid={`track-item-${track.id}`}>
            {editingTrack?.id === track.id ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div><Label>Title</Label><Input value={editingTrack.title} onChange={(e) => setEditingTrack({...editingTrack, title: e.target.value})} className="input-dark mt-1" /></div>
                  <div>
                    <Label>Album</Label>
                    <select value={editingTrack.album_id || ''} onChange={(e) => {
                      const album = albums.find(a => a.id === e.target.value);
                      setEditingTrack({...editingTrack, album_id: e.target.value, album: album?.title || ''});
                    }} className="input-dark mt-1 w-full rounded-md" data-testid="edit-track-album-select">
                      <option value="">🎵 No Album (single)</option>
                      {albums.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                    </select>
                  </div>
                  <div><Label>Price</Label><Input type="number" step="0.01" value={editingTrack.price} onChange={(e) => setEditingTrack({...editingTrack, price: parseFloat(e.target.value)})} className="input-dark mt-1" /></div>
                </div>
                <div>
                  <Label>Release Date (leave blank or set to a past date/time to release immediately)</Label>
                  <Input
                    type="datetime-local"
                    value={formatDateForInput(editingTrack.release_date)}
                    onChange={(e) => setEditingTrack({...editingTrack, release_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                    className="input-dark mt-1"
                    data-testid="edit-track-release-date"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Audio File</Label>
                    <div className="flex gap-2 mt-1">
                      <Input 
                        value={editingTrack.audio_url || ''} 
                        onChange={(e) => setEditingTrack({...editingTrack, audio_url: e.target.value})} 
                        className="input-dark flex-1" 
                        placeholder="Audio URL or upload"
                      />
                      <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                        <Upload className="w-4 h-4" />
                        {uploading.audio ? '...' : 'Upload'}
                        <input type="file" className="hidden" accept="audio/*" onChange={(e) => handleAudioUpload(e, true)} disabled={uploading.audio} />
                      </label>
                    </div>
                    {editingTrack.audio_url && <p className="text-xs text-green-500 mt-1">✓ Audio set</p>}
                  </div>
                  <div>
                    <Label>Cover Image</Label>
                    <div className="flex gap-2 mt-1">
                      <Input 
                        value={editingTrack.cover_url || ''} 
                        onChange={(e) => setEditingTrack({...editingTrack, cover_url: e.target.value})} 
                        className="input-dark flex-1" 
                        placeholder="Cover URL or upload"
                      />
                      <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                        <Upload className="w-4 h-4" />
                        {uploading.cover ? '...' : 'Upload'}
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleCoverUpload(e, true)} disabled={uploading.cover} />
                      </label>
                    </div>
                    {editingTrack.cover_url && (
                      <img src={editingTrack.cover_url} alt="Cover" className="w-12 h-12 rounded mt-1 object-cover" />
                    )}
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingTrack(null)} className="btn-ghost"><X className="w-4 h-4" /></button>
                  <button onClick={() => saveTrack(editingTrack)} className="btn-primary"><Check className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <img src={track.cover_url || '/placeholder-album.png'} alt={track.title} className="w-16 h-16 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{track.title}</h3>
                    {isScheduledRelease(track.release_date) && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Scheduled
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">{track.album_id ? track.album : 'Single (no album)'}</p>
                  {track.release_date && (
                    <p className="text-xs text-gray-500">
                      {isScheduledRelease(track.release_date) ? 'Releases' : 'Released'}: {new Date(track.release_date).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="text-right mr-4">
                  <p className="font-bold">${track.price?.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">{track.plays || 0} plays</p>
                </div>
                <div className="flex gap-2">
                  <Dialog open={showRoyaltyDialog && selectedTrackForRoyalty?.id === track.id} onOpenChange={(open) => {
                    setShowRoyaltyDialog(open);
                    if (open) setSelectedTrackForRoyalty(track);
                  }}>
                    <DialogTrigger asChild>
                      <button className="btn-ghost text-green-400" title="Distribute Royalties">
                        <DollarSign className="w-4 h-4" />
                      </button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Distribute Royalties</DialogTitle></DialogHeader>
                      <div className="space-y-4 pt-4">
                        <p>Distribute royalties to token holders of &quot;{track.title}&quot;</p>
                        <div><Label>Amount to Distribute ($)</Label><Input type="number" step="0.01" value={royaltyAmount} onChange={(e) => setRoyaltyAmount(e.target.value)} className="input-dark mt-1" placeholder="100.00" /></div>
                        <button onClick={distributeRoyalties} className="btn-primary w-full">Distribute Royalties</button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <button onClick={() => setEditingTrack({...track})} className="btn-ghost">Edit</button>
                  <button onClick={() => deleteTrack(track.id)} className="btn-ghost text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
